"use client";

import { WhatsAppGroupSend } from "./WhatsAppGroupSend";

import { useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { PencilLine } from "lucide-react";

import {
  cancelOrder,
  confirmWhatsAppPoSent,
  getWhatsAppPoDraft,
  retrySendPo,
} from "../approvals/actions";
import type { PoGroupSendView, PoSendRowView, PoSendState } from "../approvals/view";
import type { WhatsAppDraft } from "@/lib/procurement/whatsapp-handoff";
import {
  alreadyChangedBySomeoneElse,
  cancelDocLabel,
  COPY,
  couldNotSaveDetail,
  parseAlreadyLabel,
} from "../approvals/copy";
import { describeTransportError } from "../_lib/client-errors";
import { pick, type SupplyLang } from "../_lib/lang";
import { sharePoPdf } from "../_lib/po-pdf-share";
import { copyText, openWhatsAppChat } from "../_lib/whatsapp-open";
import { Toast } from "./elevated/desk-client";

export function PoSendRail({ rows, lang }: { rows: PoSendRowView[]; lang: SupplyLang }) {
  const [toast, setToast] = useState<string | null>(null);

  if (rows.length === 0) return null;

  return (
    <section aria-labelledby="vendor-po-heading" className="px-[18px] pb-6 pt-3">
      <h2
        id="vendor-po-heading"
        className="mb-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--muted)]"
      >
        {pick(lang, COPY.vendorPosHeading)}
      </h2>
      <div className="flex flex-col gap-2.5">
        {rows.map((row) => (
          <PoSendRow key={row.orderId} row={row} lang={lang} onResult={setToast} />
        ))}
      </div>
      <ResultToast message={toast} />
    </section>
  );
}

function PoSendRow({
  row,
  lang,
  onResult,
}: {
  row: PoSendRowView;
  lang: SupplyLang;
  onResult: (message: string) => void;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isSharing, setIsSharing] = useState(false);
  const pdfUrl = `/supply/po/${encodeURIComponent(row.orderId)}/pdf`;

  function send() {
    startTransition(async () => {
      try {
        const result = await retrySendPo({ orderId: row.orderId });
        onResult(
          result.ok && result.dispatch?.sent
            ? pick(lang, COPY.poEmailedToast)
            : describeSendError(result.error, lang),
        );
        router.refresh();
      } catch (error) {
        const friendly = describeTransportError(error);
        if (friendly === null) throw error;
        onResult(friendly);
      }
    });
  }

  async function share() {
    setIsSharing(true);
    try {
      const result = await sharePoPdf(pdfUrl, `${row.docCode}.pdf`);
      if (result === "shared") onResult(pick(lang, COPY.pdfReadyToShare));
      if (result === "downloaded") onResult(pick(lang, COPY.sharingUnavailableDownloaded));
    } finally {
      setIsSharing(false);
    }
  }

  return (
    <article className="rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[0_6px_18px_rgba(0,0,0,0.1)]">
      <div className="flex min-w-0 flex-col gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-baseline justify-between gap-3">
            <span className="truncate text-[12px] font-bold tabular-nums text-[var(--muted)]">
              {row.docCode}
            </span>
            <span className="shrink-0 text-[14px] font-extrabold tabular-nums text-[var(--text)]">
              {row.totalLabel ?? pick(lang, COPY.unpricedLabel)}
            </span>
          </div>
          <div className="mt-1 truncate text-[16px] font-bold text-[var(--text)]">
            {row.supplierName}
          </div>
        </div>
        <div className="flex min-w-0 flex-col items-start gap-1.5">
          <StateChip state={row.state} lang={lang} />
          {row.state.kind === "no_channel" ? (
            <p className="text-[12px] font-semibold text-[var(--muted)]">
              {pick(lang, COPY.hqOwnershipHint)}
            </p>
          ) : null}
          {row.outstandingLabel ? (
            <p className="text-[12px] font-semibold text-[var(--muted)]">
              {row.outstandingLabel}
            </p>
          ) : null}
        </div>
        <div className="rounded-[14px] border border-[var(--border-soft)] bg-[var(--raised)] p-2.5">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted)]">
            {pick(lang, COPY.pdfLabel)}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={share}
              disabled={isSharing}
              className="flex h-[40px] items-center justify-center rounded-[12px] bg-[var(--text)] px-3 text-[13px] font-bold text-[var(--inner)] transition active:scale-[0.985] disabled:opacity-60"
            >
              {isSharing ? pick(lang, COPY.preparingButton) : pick(lang, COPY.shareButton)}
            </button>
            <a
              href={pdfUrl}
              download
              className="flex h-[40px] items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--surface)] px-3 text-[13px] font-bold text-[var(--body)] transition active:scale-[0.985]"
            >
              {pick(lang, COPY.downloadLink)}
            </a>
          </div>
        </div>
        {row.editHref ? <EditBeforeSendingLink href={row.editHref} lang={lang} /> : null}
        {row.whatsapp ? <WhatsAppHandoff row={row} lang={lang} onResult={onResult} /> : null}
        {row.canSend ? (
          <button
            type="button"
            onClick={send}
            disabled={isPending}
            className="flex h-[44px] w-full items-center justify-center rounded-[14px] bg-[var(--raised)] px-4 text-[14px] font-bold text-[var(--body)] transition active:scale-[0.985] disabled:opacity-60"
          >
            {isPending
              ? row.state.kind === "not_sent"
                ? pick(lang, COPY.sendingButton)
                : pick(lang, COPY.retryingButton)
              : row.state.kind === "not_sent"
                ? pick(lang, COPY.sendButton)
                : pick(lang, COPY.retrySendButton)}
          </button>
        ) : null}
        {row.canCancel ? <CancelPoLane row={row} lang={lang} onResult={onResult} busy={isPending} /> : null}
      </div>
    </article>
  );
}

/**
 * The door to /supply/po/[orderId]/edit — change the PO before it goes out.
 * Present only when the row says so (buildPoSendView.editHref); the page makes
 * the full check itself. Not prefetched: the edit page is a heavy server
 * render, and this rail sits on the home, order and approvals pages.
 */
export function EditBeforeSendingLink({ href, lang }: { href: string; lang: SupplyLang }) {
  return (
    <Link
      href={href}
      prefetch={false}
      className="flex min-h-11 w-full items-center justify-center gap-2 rounded-[14px] bg-[var(--info-well)] px-4 text-[14px] font-bold text-[var(--info)] transition active:scale-[0.985]"
    >
      <PencilLine className="size-4" aria-hidden />
      {pick(lang, COPY.editBeforeSendingLink)}
    </Link>
  );
}

/**
 * Approver cancel for an approved/sent PO: a danger button that swaps to a
 * required-reason field (ApprovalCard's reject pattern). No outbound recall
 * exists — for a sent PO the success toast tells the operator to inform the
 * supplier themselves.
 */
function CancelPoLane({
  row,
  lang,
  onResult,
  busy,
}: {
  row: PoSendRowView;
  lang: SupplyLang;
  onResult: (message: string) => void;
  busy: boolean;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");

  function runCancel() {
    const trimmed = reason.trim();
    if (!trimmed) return;
    startTransition(async () => {
      try {
        const result = await cancelOrder({ orderId: row.orderId, reason: trimmed });
        if (result.ok) {
          setCancelling(false);
          setReason("");
          onResult(
            // The dispatch row is the last-attempt truth: the post-email
            // approved->sent CAS can lose its race, leaving a departed PO on
            // an "approved" order — so a sent-state ROW earns the
            // tell-the-supplier line even when the server flag says no.
            result.supplierAlreadySent || row.state.kind === "sent"
              ? pick(lang, COPY.poCancelledTellSupplierToast)
              : pick(lang, COPY.poCancelledToast),
          );
        } else {
          // The reason text stays in state — nothing typed is lost.
          onResult(describeCancelError(result.error, lang));
        }
        router.refresh();
      } catch (error) {
        const friendly = describeTransportError(error);
        if (friendly === null) throw error;
        onResult(friendly);
      }
    });
  }

  if (!cancelling) {
    return (
      <button
        type="button"
        onClick={() => setCancelling(true)}
        disabled={busy || isPending}
        className="flex h-[44px] w-full items-center justify-center rounded-[14px] bg-[var(--danger-well)] px-4 text-[14px] font-bold text-[var(--danger)] transition active:scale-[0.985] disabled:opacity-40"
      >
        {pick(lang, COPY.cancelPoButton)}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="block">
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--danger)]">
          {cancelDocLabel(row.docCode, lang)}
        </span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          autoFocus
          placeholder={pick(lang, COPY.cancelReasonPlaceholder)}
          // 16px floor: a focused field under 16px makes iOS zoom the page
          // and stay zoomed (cu 8fa1aec39).
          className="w-full resize-none rounded-[14px] border border-[var(--border)] bg-[var(--raised)] p-3 text-[16px] font-medium text-[var(--text)] placeholder:text-[var(--muted)] focus:outline focus:outline-2 focus:outline-[var(--danger)]"
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => {
            setCancelling(false);
            setReason("");
          }}
          disabled={isPending}
          className="flex h-[44px] items-center justify-center rounded-[14px] bg-[var(--raised)] px-3 text-[14px] font-bold text-[var(--body)] transition active:scale-[0.985] disabled:opacity-60"
        >
          {pick(lang, COPY.keepOrderButton)}
        </button>
        <button
          type="button"
          onClick={runCancel}
          disabled={isPending || reason.trim().length === 0}
          className="flex h-[44px] items-center justify-center rounded-[14px] bg-[var(--danger-well)] px-3 text-[14px] font-bold text-[var(--danger)] transition active:scale-[0.985] disabled:opacity-40"
        >
          {isPending ? pick(lang, COPY.cancellingButton) : pick(lang, COPY.cancelPoButton)}
        </button>
      </div>
    </div>
  );
}

/** Cancel soft errors: the "already <label>" race short-form expands into a
 *  full sentence (ApprovalCard's describeSoftError pattern). The rest are a
 *  MIX: guard refusals arrive as full translated sentences (they end with a
 *  full stop) and pass through untouched, but the shared plumbing strings
 *  ("cancel failed", "order not found", …) are lowercase fragments built to
 *  be wrapped — hand those to couldNotSaveDetail like ApprovalCard does, or
 *  an operator's toast literally reads "cancel failed". */
function describeCancelError(error: string | undefined, lang: SupplyLang): string {
  if (!error) return pick(lang, COPY.couldNotSaveTryAgain);
  const already = parseAlreadyLabel(error, lang);
  if (already !== null) return alreadyChangedBySomeoneElse(already, lang);
  return /[.!?]$/.test(error) ? error : couldNotSaveDetail(error, lang);
}

/**
 * The WhatsApp lane: share the PO document, then say whether it actually went.
 *
 * Sharing the generated PDF through the OS share sheet is the send. It carries
 * the real document, and it reaches the supplier GROUP chats orders actually
 * live in — which a wa.me link structurally cannot, since a link only ever
 * opens a 1:1 chat. The trade is that the operator picks the chat, so the app
 * never learns where it went; hence the confirm, and hence a dispatch row that
 * records an operator assertion rather than an observed delivery.
 */
/**
 * Share the PO PDF on WhatsApp, then record "I sent it" with the draft's
 * content key. Exported for the home / order "POs not sent yet" card, whose
 * "Already sent it" opens exactly this — there is no second way to record a
 * WhatsApp send.
 */
export function WhatsAppHandoff({
  row,
  lang,
  onResult,
}: {
  row: Pick<PoSendRowView, "orderId"> & { group?: PoGroupSendView | null };
  lang: SupplyLang;
  onResult: (message: string) => void;
}) {
  const [manualOpen, setManualOpen] = useState(false);
  const group = row.group ?? null;
  const showManual = group === null || group.mode === "offline" || (manualOpen && group.mode !== "sent" && group.mode !== "already_posted");
  const router = useRouter();
  const [draft, setDraft] = useState<WhatsAppDraft | null>(null);
  // "I sent it" is offered once the draft (and its content key) is in hand —
  // whatever the share sheet did. The send happens in WhatsApp, and the page
  // the operator comes back to has often reloaded; the PDF "Share" button
  // above never touches this lane at all. Requiring an in-session share
  // outcome here (AUD-8, 2026-09-17) left those sends with no way to be
  // recorded: zero WhatsApp confirms landed anywhere until 09-21. The server
  // still refuses a confirm with no key or a stale one (edit before sending).
  const [isPending, startTransition] = useTransition();
  const [isSharing, setIsSharing] = useState(false);

  async function loadDraft(): Promise<WhatsAppDraft | null> {
    if (draft !== null) return draft;
    const result = await getWhatsAppPoDraft({ orderId: row.orderId });
    if (!result.ok) {
      onResult(result.error);
      return null;
    }
    setDraft(result.draft);
    return result.draft;
  }

  async function shareToWhatsApp() {
    setIsSharing(true);
    try {
      const ready = await loadDraft();
      if (ready === null) return;
      const outcome = await sharePoPdf(ready.pdfUrl, ready.pdfFilename);
      if (outcome === "shared") onResult(pick(lang, COPY.pickWhatsappSupplierChat));
      if (outcome === "downloaded") {
        onResult(pick(lang, COPY.sharingUnavailableAttach));
      }
    } finally {
      setIsSharing(false);
    }
  }

  /** Optional shortcut: opens THIS supplier's 1:1 chat with the order typed. */
  function openChat() {
    startTransition(async () => {
      const ready = await loadDraft();
      if (ready?.waUrl && ready.prefillSafe) {
        // The chat opens with THIS draft's text, so its key matches what goes
        // out — but only if it really opened (a blocked popup opens nothing).
        if (!openWhatsAppChat(ready.waUrl)) onResult(pick(lang, COPY.chatNotOpened));
        return;
      }
      if (ready === null) return;
      // Too long to prefill: a truncated URL delivers half a purchase order and
      // looks complete, so copy instead.
      if (await copyText(ready.text)) {
        onResult(pick(lang, COPY.orderCopiedTooLong));
      } else {
        onResult(pick(lang, COPY.orderNotCopied));
      }
    });
  }

  function confirm() {
    startTransition(async () => {
      // The key of the draft whose PDF was shared: a PO edited since then is
      // refused server-side (edit before sending, 2026-09-17).
      const result = await confirmWhatsAppPoSent({ orderId: row.orderId, contentKey: draft?.contentKey ?? null });
      // Drop the cached draft so the next share fetches the new PDF AND a
      // fresh key — otherwise the stale key would refuse forever.
      if (result.contentChanged) setDraft(null);
      onResult(result.ok ? pick(lang, COPY.recordedSentWhatsapp) : (result.error ?? pick(lang, COPY.couldNotRecordIt)));
      router.refresh();
    });
  }

  return (
    <div className="rounded-[14px] border border-[var(--border-soft)] bg-[var(--raised)] p-2.5">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted)]">
        {pick(lang, COPY.whatsappLabel)}
      </div>
      {group ? (
        <WhatsAppGroupSend
          orderId={row.orderId}
          group={group}
          lang={lang}
          onResult={onResult}
          manualOpen={manualOpen}
          onOpenManual={() => setManualOpen(true)}
        />
      ) : null}
      {showManual ? (
        <div className={group ? "mt-2" : undefined}>
          <button
            type="button"
            onClick={shareToWhatsApp}
            disabled={isSharing || isPending}
            // Beside the group Send button, sharing by hand is the alternative, not a
            // second primary: two green buttons read as "which one?". Offline, it IS the way.
            className={group !== null && group.mode !== "offline"
              ? "flex h-[44px] w-full items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--surface)] px-3 text-[14px] font-bold text-[var(--body)] transition active:scale-[0.985] disabled:opacity-60"
              : "flex h-[44px] w-full items-center justify-center rounded-[12px] bg-[#25D366] px-3 text-[14px] font-bold text-[#04310F] transition active:scale-[0.985] disabled:opacity-60"}
          >
            {isSharing ? pick(lang, COPY.preparingPoButton) : pick(lang, COPY.sharePoWhatsappButton)}
          </button>
          {draft?.waUrl ? (
            <button
              type="button"
              onClick={openChat}
              disabled={isPending || isSharing}
              className="mt-2 flex h-[40px] w-full items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--surface)] px-3 text-[13px] font-bold text-[var(--body)] transition active:scale-[0.985] disabled:opacity-60"
            >
              {pick(lang, COPY.openTheirChatButton)}
            </button>
          ) : null}
          {draft !== null ? (
            <>
              <p className="mt-2 text-[12px] font-semibold text-[var(--muted)]">
                {pick(lang, COPY.sentMarkPoNote)}
              </p>
              <button
                type="button"
                onClick={confirm}
                disabled={isPending || isSharing}
                className="mt-2 flex h-[44px] w-full items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--surface)] px-3 text-[14px] font-bold text-[var(--body)] transition active:scale-[0.985] disabled:opacity-60"
              >
                {isPending ? pick(lang, COPY.recordingButton) : pick(lang, COPY.iSentItButton)}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StateChip({ state, lang }: { state: PoSendState; lang: SupplyLang }) {
  let label: string;
  let tone: string;

  switch (state.kind) {
    case "no_channel":
      label = pick(lang, COPY.noChannelChip);
      tone = "border border-[var(--border)] bg-[var(--raised)] text-[var(--muted)]";
      break;
    case "whatsapp_ready":
      // Deliberately not "not sent": nothing failed and nothing is pending —
      // the PO is composed and waiting for a person to press send.
      label = pick(lang, state.viaGroup ? COPY.groupReadyChip : COPY.whatsappReadyChip);
      tone = "bg-[var(--success-well)] text-[var(--success)]";
      break;
    case "not_sent":
      // NOT "Approved — not sent": the approvals queue also lists partially
      // received and received POs, and one of those with no dispatch row lands
      // here too. Calling a received PO "Approved" states the wrong status; what
      // every order reaching this state actually shares is that nothing recorded
      // a send.
      label = pick(lang, COPY.notSentChip);
      tone = "bg-[var(--amber-well)] text-[var(--accent-text)]";
      break;
    case "sending":
      label = pick(lang, COPY.sendingChip);
      tone = "bg-[var(--info-well)] text-[var(--info)]";
      break;
    case "failed":
      // shortError is dispatchVendorPo's own (out-of-scope) message — only
      // the "Send failed — " lead translates.
      label = `${pick(lang, COPY.sendFailedPrefix)} — ${state.shortError}`;
      tone = "bg-[var(--danger-well)] text-[var(--danger)]";
      break;
    case "sent":
      label = state.sentLabel;
      tone = "bg-[var(--success-well)] text-[var(--success)]";
      break;
  }

  return (
    <span
      className={`inline-flex min-h-[28px] max-w-full items-center rounded-full px-2.5 py-1 text-left text-[11px] font-bold leading-4 ${tone}`}
    >
      {label}
    </span>
  );
}

/** Internal action codes are not phone copy — translate before showing. The
 *  server now returns already-translated text for both the gate strings this
 *  used to special-case (see actions.ts's vendorOrderingOff) AND
 *  `dispatchVendorPo`'s own error text (fix round 1: `retrySendPo` routes
 *  `outcome.error` through `dispatchLibErrorCopy` before returning); only the
 *  "order id required" validation code still needs recomposing into a
 *  friendlier line, matched against the CURRENT lang's own copy value. */
function describeSendError(error: string | undefined, lang: SupplyLang): string {
  if (!error) return pick(lang, COPY.couldNotSendTryAgain);
  if (error === pick(lang, COPY.orderIdRequired)) return pick(lang, COPY.couldNotSendRefresh);
  return error;
}

function ResultToast({ message }: { message: string | null }) {
  if (!message) return null;
  return <Toast>{message}</Toast>;
}
